//RuleFrame.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Select,
  MenuItem,
  Button,
  TextField,
  FormLabel,
  Collapse,
  Alert,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCategories,
  setSelectedCategory,
  resetCategoryData,
  addCategory,
  editCategory,
  deleteCategory,
  toggleCategoryType,
  createAbnormalCategory,
} from "../reducers/ruleFrameSlice";
import NormalRuleFrame from "./NormalRuleFrame";
import AdvancedRuleFrame from "./AdvancedRuleFrame";
import { useLocation, useNavigate } from "react-router-dom";

const DANGEROUS_LEADING_CHARS = /^[=+\-@\t\r]/;

const CATEGORY_NAME_RE = /^[A-Za-z0-9 _.()-]{1,100}$/;

function validateCategoryName(rawName) {
  const name = (rawName || "").trim();

  if (!name) {
    return "Category name cannot be empty";
  }

  if (DANGEROUS_LEADING_CHARS.test(name)) {
    return "Category name cannot start with =, +, -, @, or a tab/carriage return character";
  }

  if (!CATEGORY_NAME_RE.test(name)) {
    return "Category name may only contain letters, numbers, spaces, and . _ ( )";
  }

  return null;
}

const compactFieldSx = {
  width: 220,
  "& .MuiInputBase-root": {
    fontSize: "0.8rem",
  },
  "& .MuiInputBase-input": {
    padding: "6px 8px",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.75rem",
  },
  "& .MuiFormHelperText-root": {
    position: "absolute",
    top: "100%",
    left: 0,
    margin: "2px 0 0",
    whiteSpace: "nowrap",
  },
};

const RuleFrame = () => {
  const dispatch = useDispatch();
  const { error, categories, selectedCategory, categoryType, deploymentType } =
    useSelector((state) => state.ruleFrame);

  const license_key = useSelector(
    (state) => state.authen.loggedInUser?.license_key,
  );

  const access_token = useSelector((state) => state.authen.accessToken);
  const is_admin = Boolean(access_token);

  const [newCategory, setNewCategory] = useState("");
  const [newCategoryError, setNewCategoryError] = useState("");

  const [editName, setEditName] = useState("");
  const [editNameError, setEditNameError] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const has_write = useSelector(
    (state) => state.authen.loggedInUser?.license_role === "Change",
  );

  const has_advanced = useSelector(
    (state) => state.authen.loggedInUser?.advanced_licenses?.pathology,
  );

  const location = useLocation();
  const navigate = useNavigate();

  const previousState = location.state;

  // Load categories initially
  useEffect(() => {
    if (license_key) {
      dispatch(fetchCategories(license_key));
    }
  }, [dispatch, license_key]);

  const isBasicMode =
    selectedCategory && categoryType[selectedCategory] === false;

  const handleBack = () => {
    if (previousState?.from) {
      navigate(previousState.from, {
        state: previousState,
      });
    } else {
      navigate(-1);
    }
  };

  const handleNewCategoryChange = (e) => {
    const value = e.target.value;
    setNewCategory(value);
    // Clear the error as soon as the user edits the field
    if (newCategoryError) setNewCategoryError("");
  };

  const handleAddCategory = () => {
    const trimmed = newCategory.trim();
    const validationError = validateCategoryName(trimmed);
    if (validationError) {
      setNewCategoryError(validationError);
      return;
    }
    dispatch(addCategory({ category: trimmed, license_key }));
    setNewCategory("");
    setNewCategoryError("");
  };

  const handleEditNameChange = (e) => {
    const value = e.target.value;
    setEditName(value);
    if (editNameError) setEditNameError("");
  };

  const handleSaveEdit = () => {
    const trimmed = editName.trim();
    const validationError = validateCategoryName(trimmed);
    if (validationError) {
      setEditNameError(validationError);
      return;
    }
    dispatch(
      editCategory({
        oldName: selectedCategory,
        newName: trimmed,
        license_key,
      }),
    );
    setIsEditing(false);
    setEditNameError("");
  };

  const handleLegacyReset = () => {
    dispatch(resetCategoryData({ license_key, access_token }))
      .unwrap()
      .then(({ blob, filename, validationPassed }) => {
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);

        if (validationPassed === "True") {
          // show success notification
        } else {
          // show whatever validation result is appropriate
        }
      })
      .catch((error) => {
        // show error notification
      });
  };

  const handleCreateAbnormalCategory = () => {
    if (!selectedCategory) return;

    dispatch(
      createAbnormalCategory({ category: selectedCategory, license_key }),
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {/* First Paper */}
      <Paper
        elevation={1}
        sx={{
          px: 2,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          flexWrap: "wrap",
          minHeight: 44,
        }}
      >
        <FormLabel sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>
          Pathology Test Categories
        </FormLabel>

        <Select
          size="small"
          sx={{
            width: 240,
            "& .MuiInputBase-input": {
              fontSize: "0.8rem",
              padding: "6px 8px",
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.75rem",
            },
          }}
          value={selectedCategory || ""}
          onChange={(e) => {
            dispatch(setSelectedCategory(e.target.value));
            setIsEditing(false); // reset edit mode if switching
            setEditNameError("");
          }}
        >
          {categories.map((cat) => (
            <MenuItem key={cat} value={cat}>
              {cat}
            </MenuItem>
          ))}
        </Select>

        {deploymentType !== "runner" && (
          <>
            <Button
              size="small"
              variant="dense"
              color="secondary"
              onClick={handleCreateAbnormalCategory}
              disabled={!selectedCategory || !has_advanced || !has_write}
            >
              Create Abnormal Rule
            </Button>

            <Button
              size="small"
              variant="dense"
              color="error"
              onClick={() => {
                const result = window.confirm(
                  `Are you sure you want to delete ${selectedCategory}?`,
                );
                if (result) {
                  dispatch(
                    deleteCategory({ category: selectedCategory, license_key }),
                  );
                } else {
                  window.alert("The action has been cancelled.");
                }
              }}
              disabled={!selectedCategory || !has_write}
            >
              Delete
            </Button>

            {!isEditing ? (
              <Button
                size="small"
                variant="dense"
                onClick={() => {
                  setIsEditing(true);
                  setEditName(selectedCategory); // pre-fill with old name
                  setEditNameError("");
                }}
                disabled={!selectedCategory || !has_write}
              >
                Edit
              </Button>
            ) : (
              <>
                <Box sx={{ position: "relative" }}>
                  <TextField
                    size="small"
                    label="New Name"
                    value={editName}
                    onChange={handleEditNameChange}
                    error={Boolean(editNameError)}
                    helperText={editNameError || undefined}
                    sx={compactFieldSx}
                  />
                </Box>
                <Button
                  size="small"
                  variant="dense"
                  color="primary"
                  onClick={handleSaveEdit}
                  disabled={!has_write}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  variant="dense"
                  onClick={() => {
                    setIsEditing(false);
                    setEditNameError("");
                  }}
                >
                  Cancel
                </Button>
              </>
            )}

            <Box sx={{ position: "relative" }}>
              <TextField
                size="small"
                label="New Category"
                value={newCategory}
                onChange={handleNewCategoryChange}
                disabled={!has_write}
                error={Boolean(newCategoryError)}
                helperText={newCategoryError || undefined}
                sx={compactFieldSx}
              />
            </Box>
            <Button
              size="small"
              variant="dense"
              color="primary"
              disabled={!has_write}
              onClick={handleAddCategory}
            >
              Add
            </Button>
          </>
        )}

        {selectedCategory && (
          <Button
            size="small"
            variant="dense"
            color={isBasicMode ? "secondary" : "primary"}
            disabled={!has_write || (isBasicMode && !has_advanced)}
            onClick={() => dispatch(toggleCategoryType(selectedCategory))}
          >
            {isBasicMode ? "Switch to Advanced Mode" : "Switch to Basic Mode"}
          </Button>
        )}

        {has_write && is_admin && (
          <Button
            size="small"
            variant="dense"
            color="secondary"
            onClick={() => {
              const result = window.confirm(
                "Are you sure you want to perform a Legacy Reset?",
              );

              if (result) {
                handleLegacyReset();
              } else {
                window.alert("The action has been cancelled.");
              }
            }}
          >
            Legacy Reset
          </Button>
        )}

        <Button
          size="small"
          variant="dense"
          onClick={() =>
            navigate(previousState?.from ?? -1, {
              state: previousState,
            })
          }
        >
          Back
        </Button>

        <Collapse in={Boolean(error)}>
          {error && (
            <Alert
              severity="error"
              variant="filled"
              sx={{ py: 0, fontSize: "0.75rem" }}
            >
              {error}
            </Alert>
          )}
        </Collapse>
      </Paper>

      {isBasicMode && <NormalRuleFrame />}
      {!isBasicMode && <AdvancedRuleFrame />}
    </Box>
  );
};

export default RuleFrame;
